import { AnyAction } from "redux";

export interface DraftStep {
  step: string;
  status: "Pending" | "In Progress" | "Completed" | "Failed";
  messages?: string[];
}

export interface DraftJob {
  draftId: string;
  correlationId: string;
  isConnecting: boolean;
  isDraftGenerating: boolean;
  status: "Queued" | "In Progress" | "Completed" | "Failed" | "Cancelled";
  progress: DraftStep[];
  serviceIcon?: string;
  drafts: { [key: string]: any };
  content?: string;
  version?: number;
  finalStatus?: string;
}

interface DraftState {
  jobs: {
    [caseId: string]: {
      [draftId: number]: DraftJob;
    };
  };
}


const blankDraftJob = (draftId: string, correlationId: string): DraftJob => ({
  draftId,
  correlationId,
  isConnecting: false,
  isDraftGenerating: false,
  status: "Queued",
  progress: [],
  drafts: []
});

const mergeStep = (progress: DraftStep[], patch: DraftStep): DraftStep[] => {
  const idx = progress.findIndex((s) => s.step === patch.step);
  if (idx >= 0) {
    const existing = progress[idx];
    const updated = {
      ...existing,
      status: patch.status,
      messages: [...(existing.messages || []), ...(patch.messages || [])],
    };
    return [...progress.slice(0, idx), updated, ...progress.slice(idx + 1)];
  }
  return [...progress, patch];
};


const initialState: DraftState = {
  jobs: {},
};

export const draftReducer = (state = initialState, action: AnyAction): DraftState => {
  const caseId = action.payload?.caseId;
  const draftId = action.payload?.draftId;
  const correlationId = action.payload?.correlationId;

  if (!caseId || !draftId) return state;

  const existingJob: DraftJob =
    state.jobs?.[caseId]?.[draftId] ?? blankDraftJob(draftId, correlationId || "");

  const baseJob: DraftJob = correlationId
    ? { ...existingJob, correlationId }
    : existingJob;

  switch (action.type) {
    case "DRAFT_JOB_CONNECTING":
      return {
        jobs: {
          ...state.jobs,
          [caseId]: {
            ...(state.jobs[caseId] || {}),
            [draftId]: { ...baseJob, isConnecting: true },
          },
        },
      };

    case "DRAFT_JOB_STARTED":
      return {
        jobs: {
          ...state.jobs,
          [caseId]: {
            ...(state.jobs[caseId] || {}),
            [draftId]: {
              ...baseJob,
              isConnecting: false,
              isDraftGenerating: true,
              status: "In Progress",
            },
          },
        },
      };

    case "DRAFT_PROGRESS_UPDATE": {
      const newProgress = Array.isArray(action.payload.progress)
        ? action.payload.progress
        : mergeStep(baseJob.progress, action.payload);

      return {
        jobs: {
          ...state.jobs,
          [caseId]: {
            ...(state.jobs[caseId] || {}),
            [draftId]: {
              ...baseJob,
              isDraftGenerating: true,
              progress: newProgress,
            },
          },
        },
      };
    }

    case "DRAFT_GENERATION_COMPLETE":
      return {
        jobs: {
          ...state.jobs,
          [caseId]: {
            ...(state.jobs[caseId] || {}),
            [draftId]: {
              ...baseJob,
              isDraftGenerating: false,
              isConnecting: false,
              status: "Completed",
            },
          },
        },
      };

    case "DRAFT_GENERATION_FAILED":
      return {
        jobs: {
          ...state.jobs,
          [caseId]: {
            ...(state.jobs[caseId] || {}),
            [draftId]: {
              ...baseJob,
              isDraftGenerating: false,
              isConnecting: false,
              status: "Failed",
            },
          },
        },
      };

    case "DRAFT_CANCELLED":
      return {
        jobs: {
          ...state.jobs,
          [caseId]: {
            ...(state.jobs[caseId] || {}),
            [draftId]: {
              ...baseJob,
              isDraftGenerating: false,
              isConnecting: false,
              status: "Cancelled",
            },
          },
        },
      };


    case "UPDATE_CASE_DRAFT": {
      const { draftId, content, version, caseId, status } = action.payload;
    
      const isComplete = Boolean(content && version);
      const existingCorrelationId = localStorage.getItem(`draft:correlation:${draftId}`);
      const isDraftGenerating = !isComplete && Boolean(existingCorrelationId);
    
      const current = state.jobs?.[caseId]?.[draftId] ?? blankDraftJob(draftId, existingCorrelationId || "");
    
      return {
        jobs: {
          ...state.jobs,
          [caseId]: {
            ...(state.jobs[caseId] || {}),
            [draftId]: {
              ...current,
              draftId,
              content,
              version,
              finalStatus: status || current.finalStatus,
              isDraftGenerating,
              status: isComplete ? "Completed" : current.status,
            },
          },
        },
      };
    }

    case "UPDATE_CASE_DRAFT_STATUS":
      return {
        jobs: {
          ...state.jobs,
          [caseId]: {
            ...(state.jobs[caseId] || {}),
            [draftId]: {
              ...baseJob,
              finalStatus: action.payload.status,
            },
          },
        },
      };

    default:
      return state;
  }
};

